// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Spacer, Typography } from "@aviary";
import { EmersonProductCard } from "@emerson/components/EmersonProductCard/EmersonProductCard";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { useCheckSimilarProductsAvailableQuery } from "@emerson/data/queries/CheckSimilarProductsAvailable/CheckSimilarProductsAvailable.query";
import { useSimilarProductsEmersonQuery } from "@emerson/data/queries/SimilarProductsQuery/SimilarProducts.query.generated";
import { l } from "@emerson/locales/i18n";
import {
  EmersonViewProductEntryPoint,
  EmersonCheckoutStartEntryPoint,
  EmersonCheckoutAddToCartEntryPoint,
} from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { SkeletonSimilarProducts } from "./SkeletonSimilarProducts";

interface Props {
  productId: string;
  similarProductsRef: <T extends HTMLDivElement>(el: T) => void;
}

const SimilarProducts = ({ productId, similarProductsRef }: Props) => {
  const { t } = useTranslation();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const { data: availabilityData } = useCheckSimilarProductsAvailableQuery({
    variables: { productId },
  });

  const [loadSimilar, setLoadSimilar] = useState(false);

  useEffect(() => {
    if (availabilityData?.viewer?.product?.anySimilarProductAvailable) {
      setLoadSimilar(true);
    }
  }, [availabilityData?.viewer?.product?.anySimilarProductAvailable]);

  const { data, loading } = useSimilarProductsEmersonQuery({
    skip: !loadSimilar,
    variables: { productId, first: 4, isUnauthenticated },
  });

  if (loading) return <SkeletonSimilarProducts />;
  if (
    !(
      data?.viewer?.product &&
      "similarProducts" in data.viewer.product &&
      data.viewer.product.similarProducts?.nodes?.length
    )
  ) {
    return null;
  }

  const similarProducts = data.viewer.product.similarProducts.nodes ?? [];

  const renderProducts = () => {
    return similarProducts.map((product, index) => {
      const searchRank = index + 1;

      return (
        <EmersonProductCard
          key={product?.id}
          entryPoint={EmersonViewProductEntryPoint.SIMILAR_PRODUCT_CARD}
          checkoutStartEntryPoint={
            EmersonCheckoutStartEntryPoint.PRODUCTPAGE_SIMILAR_PRODUCTCARD_VIEWCART
          }
          checkoutAddToCartEntryPoint={EmersonCheckoutAddToCartEntryPoint.SIMILAR_PRODUCT_CARD}
          variant={product.availableOrMasterVariant}
          product={product}
          brandName={product?.brand?.name}
          searchRank={searchRank}
          to={emersonRoutes.getPDP(
            product?.brand?.name,
            product?.slug,
            product?.id,
            product?.availableOrMasterVariant?.emersonVariantId
          )}
          noFilterColumn
        />
      );
    });
  };

  return (
    <div ref={similarProductsRef}>
      <Typography type="h2">{t(l.common.SimilarProducts)}</Typography>
      <Spacer height="one" />
      <Columns>{renderProducts()}</Columns>
    </div>
  );
};

export { SimilarProducts };
